import React, { useEffect } from "react";
import styled from "styled-components";
import { Layout } from "../components/layout/Layout";
import { MediaGrid } from "../components/media/MediaGrid";
import { MediaCard } from "../components/media/MediaCard";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { Movie, TVShow, User } from "../types";
import { useQuery } from "@tanstack/react-query";
import { tmdbApi } from "../services/tmdb";
import { toggleMovieWatchlist, toggleShowWatchlist } from "../store/slices/mediaSlice";
import { watchlistService } from "../services/watchlistService";
import { useTranslation } from "../hooks/useTranslation";
import { SEOHead } from "../components/seo/SEOHead";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import HighPerformanceBanner from '../components/ads/HighPerformanceBanner';
import HilltopAd from '../components/ads/HilltopAd';

const WatchlistContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: var(--spacing-4);
`;

const Header = styled.header`
  margin-bottom: var(--spacing-6);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  background: var(--background-secondary);
  border-radius: var(--border-radius);
`;

const Section = styled.section`
  margin-bottom: var(--spacing-6);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: var(--spacing-4);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const VideoAdWrapper = styled.div`
  border: 2px solid blue;
  padding: 10px;
  margin: 20px 0;
  background-color: #f0f8ff;
`;

export const Watchlist = () => {
  const user = useSelector((state: RootState) => state.auth.user) as User | null;
  const dispatch = useDispatch();
  const watchlist = useSelector((state: RootState) => state.media.watchlist);
  
  // Scroll automatique vers le haut au chargement
  useScrollToTopOnMount();

  // Load watchlist when component mounts
  useEffect(() => {
    if (user?.uid) {
      watchlistService.loadWatchlist(user.uid);
    }
  }, [user?.uid]);

  const { data: movies, isLoading: moviesLoading } = useQuery({
    queryKey: ["watchlistMovies", watchlist.movies],
    queryFn: async () => {
      if (!watchlist.movies?.length) return [];
      const moviePromises = watchlist.movies.map(item =>
        tmdbApi.getDetails("movie", item.id)
      );
      return Promise.all(moviePromises);
    },
    enabled: !!watchlist.movies?.length,
  });

  const { data: shows, isLoading: showsLoading } = useQuery({
    queryKey: ["watchlistShows", watchlist.shows],
    queryFn: async () => {
      if (!watchlist.shows?.length) return [];
      const showPromises = watchlist.shows.map(item =>
        tmdbApi.getDetails("tv", item.id)
      );
      return Promise.all(showPromises);
    },
    enabled: !!watchlist.shows?.length,
  });

  const handleDelete = async (type: 'movie' | 'tv', id: number) => {
    if (!user?.uid) return;
    
    try {
      if (type === 'movie') {
        await watchlistService.toggleMovie(user.uid, id);
        dispatch(toggleMovieWatchlist(id));
      } else {
        await watchlistService.toggleShow(user.uid, id);
        dispatch(toggleShowWatchlist(id));
      }
    } catch (error) {
      console.error('Error removing item from watchlist:', error);
      alert('Une erreur est survenue lors de la suppression');
    }
  };

  const isLoading = moviesLoading || showsLoading;

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  const favoriteMovies = movies?.filter((movie, index) => {
    const watchlistItem = watchlist.movies.find(item => item.id === movie.id);
    return watchlistItem && !watchlistItem.watchLater;
  }) || [];

  const watchLaterMovies = movies?.filter((movie, index) => {
    const watchlistItem = watchlist.movies.find(item => item.id === movie.id);
    return watchlistItem && watchlistItem.watchLater;
  }) || [];

  const favoriteShows = shows?.filter((show, index) => {
    const watchlistItem = watchlist.shows.find(item => item.id === show.id);
    return watchlistItem && !watchlistItem.watchLater;
  }) || [];

  const watchLaterShows = shows?.filter((show, index) => {
    const watchlistItem = watchlist.shows.find(item => item.id === show.id);
    return watchlistItem && watchlistItem.watchLater;
  }) || [];

  const hasContent = favoriteMovies.length > 0 || favoriteShows.length > 0 ||
                    watchLaterMovies.length > 0 || watchLaterShows.length > 0;

  return (
    <Layout>
      <WatchlistContainer>
        {/* Publicité HighPerformance en haut */}
        <HighPerformanceBanner 
          adKey="2154f7f7df439957dfc24240cd37dd3c"
          width={728}
          height={90}
          id="hp-banner-watchlist-top"
          hideOnMobile
        />
        
        {/* Publicité HilltopAds */}
        <HilltopAd id="hilltop-watchlist-1" />
        
        <Header>
          <Title>Ma Liste</Title>
        </Header>

        {!hasContent ? (
          <EmptyState>
            <p>Vous n'avez pas encore ajouté de contenu à votre liste</p>
          </EmptyState>
        ) : (
          <>
            {favoriteMovies.length > 0 && (
              <Section>
                <SectionTitle>Films favoris</SectionTitle>
                <MediaGrid 
                  items={favoriteMovies} 
                  type="movie" 
                  showRemoveButton={true}
                  onRemove={(id) => handleDelete('movie', id)}
                  isInWatchlist={true}
                />
              </Section>
            )}

            {watchLaterMovies.length > 0 && (
              <Section>
                <SectionTitle>Films à voir plus tard</SectionTitle>
                <MediaGrid 
                  items={watchLaterMovies} 
                  type="movie" 
                  showRemoveButton={true}
                  onRemove={(id) => handleDelete('movie', id)}
                  isInWatchlist={true}
                />
              </Section>
            )}

            {favoriteShows.length > 0 && (
              <Section>
                <SectionTitle>Séries favorites</SectionTitle>
                <MediaGrid 
                  items={favoriteShows} 
                  type="tv" 
                  showRemoveButton={true}
                  onRemove={(id) => handleDelete('tv', id)}
                  isInWatchlist={true}
                />
              </Section>
            )}

            {watchLaterShows.length > 0 && (
              <Section>
                <SectionTitle>Séries à voir plus tard</SectionTitle>
                <MediaGrid 
                  items={watchLaterShows} 
                  type="tv" 
                  showRemoveButton={true}
                  onRemove={(id) => handleDelete('tv', id)}
                  isInWatchlist={true}
                />
              </Section>
            )}
          </>
        )}
      </WatchlistContainer>
      
      {/* Publicité HighPerformance en bas */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '1rem 0', background: 'var(--background-primary)' }}>
        <HighPerformanceBanner 
          adKey="2154f7f7df439957dfc24240cd37dd3c"
          width={728}
          height={90}
          id="hp-banner-watchlist-bottom"
        />
      </div>
    </Layout>
  );
};

export default Watchlist;
